// ##################### Finding View V2 #####################
import { DBDef_FindingView, findingViewSectionType_ByOrder, findingViewSectionType_FishBone, findingViewSectionType_Flat, findingViewSectionType_Graph, findingViewType_Parent, findingViewType_Sections, } from '@app/advisor-ui-shared/index';
import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { _keys, _values, arrayIncludesAll, currentTimeMillis, filterInstances, md5, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { ModuleFE_AdvisorStatic } from '../../../ModuleFE_AdvisorStatic.js';
import { FindingType_Range, FindingType_Textual } from '@app/advisor-shared';
import { ModuleFE_Advisor_ContextRule, ModuleFE_Advisor_Variable } from '../../healthcare-space.js';
import { AdvisorConfigKeyFE_FindingView_ParentOrder } from '../../app-config.js';
import { filterFindingsByContextRules, getSectionConfigFilteredFindings, groupFindingsIntoResults, groupFindingsIntoResultsByOrder, sortFindingResults, sortSpecialAttentionResultsToStart } from './utils.js';
import { ModuleFE_RuntimeFinding } from '../../../../_entity/runtime/runtime-finding/index.js';
import { convertFishBone_100_101 } from '@app/advisor-ui-shared/_enum/fishbone/consts';
export const dispatch_onFindingViewsUpdatedV2 = new ThunderDispatcherV3('__onFindingViewsUpdated');
class ModuleFE_Advisor_FindingView_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_FindingView, dispatch_onFindingViewsUpdatedV2);
        this.registerVersionUpgradeProcessor('1.0.1', this.upgrade101);
        this.registerVersionUpgradeProcessor('1.0.3', this.upgrade_103);
    }
    variableMatches = {
        view: (variable, view) => {
            if (view.type === findingViewType_Parent)
                return false;
            //Find if the variable appears in any of the sections
            return view.sections.some(section => this.variableMatches.section(variable, section));
        },
        section: (variable, section) => {
            switch (section.type) {
                case findingViewSectionType_Flat:
                case findingViewSectionType_ByOrder:
                    return this.variableMatches.sectionRules(variable, section);
                case findingViewSectionType_FishBone: {
                    const struct = section.data.fishbone;
                    if (!struct)
                        return false;
                    //Check if the fishbone struct includes the variable associated with the finding
                    const varIds = new Set();
                    struct.head?.variableIds?.forEach(id => varIds.add(id));
                    struct.tail?.variableIds?.forEach(id => varIds.add(id));
                    struct.ribs.forEach(rib => rib.ribItems.forEach(item => item.variableIds?.forEach(id => varIds.add(id))));
                    return varIds.has(variable._id);
                }
                case findingViewSectionType_Graph:
                    //Check if the graph include the finding varId
                    return section.data.variableIds.includes(variable._id);
                default:
                    // @ts-ignore
                    this.logError(`Check missing for section of type ${section.type}`);
                    return false;
            }
        },
        sectionRules: (variable, section) => {
            const contextRule = ModuleFE_Advisor_ContextRule.cache.unique(section.data.contextRuleId);
            if (!contextRule)
                return false;
            //this section blacklists the variable
            if (contextRule.excludedVariableIds.includes(variable._id))
                return false;
            for (const rule of contextRule.rules) {
                //Continue to next rule if the variable does not match the rule types
                if (rule.variableTypes?.length && !rule.variableTypes.includes(variable.type))
                    continue;
                //If the variable passes the tagIds of the rule, it appears in the section, therefore it appears in the view
                if (arrayIncludesAll(variable._tagIds, rule.tagIds))
                    return true;
            }
            return false;
        }
    };
    findingMatches = {
        view: (finding, view) => {
            if (view.type === findingViewType_Parent)
                return false;
            //Find if the finding appears in any of the sections
            return view.sections.some(section => this.findingMatches.section(finding, section));
        },
        section: (finding, section) => {
            const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
            if (!variable) {
                this.logDebugBold(`Could not find variable for id ${finding.variableId}`, finding);
                return false;
            }
            return this.variableMatches.section(variable, section);
        },
        sectionRules: (finding, section) => {
            const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
            if (finding.type === FindingType_Range)
                return false;
            if (!variable) {
                this.logDebugBold(`Could not find variable for id ${finding.variableId}`, finding);
                return false;
            }
            return this.variableMatches.sectionRules(variable, section);
        }
    };
    getFindingsForSection = {
        flat: (section, onlyAbnormalResults) => {
            //Get findings
            let findings = getSectionConfigFilteredFindings(section.config, onlyAbnormalResults);
            //Filter findings for context rule
            findings = filterFindingsByContextRules(findings, section.data.contextRuleId);
            //Group findings into finding results
            let findingResults = groupFindingsIntoResults(findings);
            //Sort findings results
            findingResults = sortFindingResults(findingResults, section.data.findingSortMethod);
            //Sort special attention results to the start
            findingResults = sortSpecialAttentionResultsToStart(findingResults);
            return findingResults;
        },
        byOrder: (section) => {
            //Get findings
            let findings = getSectionConfigFilteredFindings(section.config);
            //Filter findings for context rule
            findings = filterFindingsByContextRules(findings, section.data.contextRuleId);
            //Push textual findings to the start
            sortArray(findings, f => f.type === FindingType_Textual ? -1 : 1);
            //Group findings into results
            return groupFindingsIntoResultsByOrder(findings, section.data.findingSortMethod, section.data.orderContextRuleId);
        },
        fishbone: (section) => {
            const struct = section.data.fishbone;
            if (!struct)
                return [];
            const varIds = new Set();
            struct.head?.variableIds?.forEach(id => varIds.add(id));
            struct.tail?.variableIds?.forEach(id => varIds.add(id));
            struct.ribs.forEach(rib => rib.ribItems.forEach(item => item.variableIds?.forEach(id => varIds.add(id))));
            const findings = ModuleFE_RuntimeFinding.cache.filter(finding => varIds.has(finding.variableId));
            sortArray(findings, finding => finding.issuedTimestamp ?? finding.__created, true);
            const results = findings.reduce((map, finding) => {
                if (!map[finding.variableId]) {
                    const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
                    if (!variable)
                        throw new MUSTNeverHappenException(`Could not find variable for id ${finding.variableId}`);
                    map[finding.variableId] = { variable, findings: [] };
                }
                map[finding.variableId].findings.push(finding);
                return map;
            }, {});
            return _values(results);
        },
        graph: (section) => {
            const varIds = section.data.variableIds;
            const findings = ModuleFE_RuntimeFinding.cache.filter(finding => finding.type === 'numeric' && varIds.includes(finding.variableId));
            sortArray(findings, finding => finding.issuedTimestamp ?? finding.__created, true);
            const results = findings.reduce((map, finding) => {
                if (!map[finding.variableId]) {
                    const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
                    if (!variable)
                        throw new MUSTNeverHappenException(`Could not find variable for id ${finding.variableId}`);
                    map[finding.variableId] = { variable, findings: [] };
                }
                map[finding.variableId].findings.push(finding);
                return map;
            }, {});
            return _values(results);
        }
    };
    isVisible = {
        view: (_view) => {
            const view = typeof _view === 'string' ? this.cache.unique(_view) : _view;
            if (!view)
                return false;
            switch (view.type) {
                case findingViewType_Parent: {
                    return view.childrenIds.some(childId => this.isVisible.view(childId));
                }
                case findingViewType_Sections: {
                    return view.sections.some(section => this.isVisible.section(section));
                }
                default:
                    return false;
            }
        },
        section: (section) => {
            if (section.config.alwaysVisible)
                return true;
            switch (section.type) {
                case findingViewSectionType_Flat:
                    return !!this.getFindingsForSection.flat(section).length;
                case findingViewSectionType_ByOrder:
                    return !!this.getFindingsForSection.byOrder(section).length;
                case findingViewSectionType_FishBone:
                    return !!_keys(this.getFindingsForSection.fishbone(section)).length;
                default:
                    return false;
            }
        }
    };
    getTopLevelViews = () => {
        const parentViews = this.cache.filter(view => view.type === findingViewType_Parent);
        const childrenIds = parentViews.map(view => view.childrenIds).flat();
        const parentlessViews = this.cache.filter(view => view.type !== findingViewType_Parent && !childrenIds.includes(view._id));
        const toRet = [...parentViews, ...parentlessViews];
        const parentOrder = AdvisorConfigKeyFE_FindingView_ParentOrder.get();
        if (parentOrder)
            sortArray(toRet, view => {
                const index = parentOrder.indexOf(view._id);
                if (index !== -1)
                    return index;
                return toRet.length;
            });
        return toRet;
    };
    getTimestampForSection = (section) => {
        switch (section.type) {
            case findingViewSectionType_Flat: {
                const results = this.getFindingsForSection.flat(section);
                if (!results.length)
                    return 0;
                return results[0].findings[0]?.__created ?? 0;
            }
            case findingViewSectionType_ByOrder: {
                const results = this.getFindingsForSection.byOrder(section);
                if (!results.length)
                    return 0;
                return results[0].blocks[0]?.rtOrder?.__created ?? 0;
            }
            case findingViewSectionType_FishBone: {
                const results = this.getFindingsForSection.fishbone(section);
                if (!results.length)
                    return 0;
                return Math.max(...results.map(result => result.findings[0].__created));
            }
            case findingViewSectionType_Graph: {
                return 0;
            }
        }
    };
    upgrade101 = async (items) => {
        const contextRulesToCreate = [];
        for (const item of items) {
            // remove irrelevant fields
            delete item.__variableIds;
            delete item.__tagIds;
            if (item.type === findingViewType_Parent)
                continue;
            for (const section of item.sections) {
                if (![findingViewSectionType_Flat, findingViewSectionType_ByOrder].includes(section.type))
                    continue;
                const _section = section;
                const rules = [..._section.data.rules ?? []];
                const blacklistVariableIds = _section.data.blacklistVariableIds ?? [];
                delete _section.data.rules;
                delete _section.data.blacklistVariableIds;
                const currentTime = currentTimeMillis();
                const dbContextRule = {
                    _id: md5(`${item._id}-${section.label}`),
                    label: `${item.label} - FV Context Rules`,
                    rules,
                    excludedVariableIds: blacklistVariableIds,
                    _v: '1.0.0',
                    __updated: currentTime,
                    __created: currentTime,
                    _tagIds: filterInstances(rules.flatMap(rule => rule.tagIds)),
                };
                contextRulesToCreate.push(dbContextRule);
                section.data.contextRuleId = dbContextRule._id;
            }
            await ModuleFE_Advisor_ContextRule.onEntriesUpdated(contextRulesToCreate);
        }
    };
    upgrade_103 = async (items) => {
        items.forEach(item => {
            if (item.type !== findingViewType_Sections)
                return;
            item.sections.forEach(section => {
                if (section.type === findingViewSectionType_FishBone && section.data.fishbone)
                    convertFishBone_100_101(section.data.fishbone);
            });
        });
    };
}
export const ModuleFE_Advisor_FindingView = new ModuleFE_Advisor_FindingView_Class();
//# sourceMappingURL=module.js.map